import { Controller } from "@hotwired/stimulus"

// Walks the admin through the Doctolib import: Doctolib blocks server requests,
// so the admin opens the profile, copies it with the bookmarklet and clicks to
// paste it here. The server parses the page and streams the result into the recap; the
// "apply" button then fills the practitioner fields that are still empty.
const DOCTOLIB_URL = /^https:\/\/www\.doctolib\.fr\/[a-z0-9-]+\/[a-z0-9-]+\/[a-z0-9-]+$/
const AVATAR_TYPES = ["image/jpeg", "image/png"]
const OFFICE_TEXT_FIELDS = ["phone", "floor", "access_information", "means_of_transport"]
const OFFICE_CHECKBOXES = ["elevator", "pmr"]
const RECORD_PROVIDER_FIELDS = ["education_provider_id", "custom_education_provider"]
// Mirrors +EducationRecord::OTHER_EDUCATION_PROVIDER+.
const OTHER_EDUCATION_PROVIDER = "other"

// Mirrors the +doctolib_url+ normalization of +Profiles::Practitioner+.
export function normalizeDoctolibUrl (value) {
  return (value || "")
    .trim()
    .replace(/^(?:https?:\/\/)?(?:pro|partners|www)\.doctolib\.fr/i, "https://www.doctolib.fr")
    .replace(/[?#].*$/, "")
    .replace(/\/$/, "")
}

export function validDoctolibUrl (value) {
  const url = normalizeDoctolibUrl(value)
  return DOCTOLIB_URL.test(url) ? url : null
}

export default class extends Controller {
  static targets = ["step", "consent", "openButton", "paste", "result", "payload", "applied"]
  static values = {
    url: String,
    urlField: String,
    objectName: String,
    messages: { type: Object, default: {} }
  }

  connect () {
    this.form = this.element.closest("form") || this.element
    this.opened = false
    this.imported = false
    // Create and edit modals can both be in the page: the stream needs an id
    // no other recap uses.
    this.resultTarget.id = `doctolib-import-${Math.random().toString(36).slice(2, 12).padEnd(6, "0")}`
    this.refresh = this.refresh.bind(this)
    this.form.addEventListener("input", this.refresh)
    this.refresh()
  }

  disconnect () {
    this.form.removeEventListener("input", this.refresh)
  }

  get doctolibUrl () {
    const input = this.form.querySelector(`[id="${this.urlFieldValue}"]`)
    return validDoctolibUrl(input?.value)
  }

  refresh () {
    const hasUrl = Boolean(this.doctolibUrl)
    const agreed = this.consentTarget.checked

    // Opening needs a profile URL. Pasting does not: the page was already
    // copied, and a template or empty URL must not block the import.
    this.openButtonTarget.disabled = !(hasUrl && agreed)
    this.pasteTarget.disabled = !agreed
    this.markStep("url", hasUrl)
    this.markStep("consent", agreed)
    this.markStep("open", hasUrl && agreed && this.opened)
    this.markStep("paste", this.imported)
  }

  openProfile () {
    const url = this.doctolibUrl
    if (!url) return

    // Doctolib sends visitors with an external referrer to the booking funnel.
    window.open(url, "_blank", "noopener,noreferrer")
    this.opened = true
    this.refresh()
  }

  explainBookmarklet (event) {
    event.preventDefault()
    window.alert(this.messagesValue.drag)
  }

  async paste () {
    if (this.pasteTarget.disabled) return

    let html = ""
    try {
      html = await navigator.clipboard.readText()
    } catch (_error) {
      // readText() is the only permission prompt. Browsers expose no link that
      // reopens it once the admin has refused.
      this.showStatus(this.messagesValue.clipboardDenied, "text-danger", "fa-circle-exclamation")
      return
    }

    if (!html.trim().startsWith("<")) {
      this.showStatus(this.messagesValue.notPasted, "text-danger", "fa-circle-exclamation")
      return
    }

    this.submit(html)
  }

  async submit (html) {
    this.imported = false
    this.refresh()
    this.showStatus(this.messagesValue.analyzing, "text-muted", "fa-spinner fa-spin")

    const body = new FormData()
    body.append("html", html)
    body.append("target", this.resultTarget.id)
    body.append("expected_url", this.doctolibUrl || "")

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": csrfToken() },
        credentials: "same-origin",
        body
      })
      if (!(response.headers.get("Content-Type") || "").includes("turbo-stream")) throw new Error(response.statusText)

      window.Turbo.renderStreamMessage(await response.text())
      this.imported = response.ok
    } catch (_error) {
      this.showStatus(this.messagesValue.error, "text-danger", "fa-circle-exclamation")
    }

    this.refresh()
  }

  async apply () {
    if (!this.hasPayloadTarget) return

    this.scrollAnchor = null
    const payload = JSON.parse(this.payloadTarget.dataset.payload || "{}")
    // Each step on its own: a field that throws (editor, select) must not skip the office.
    // The avatar downloads last so a slow photo does not delay the office.
    const groups = [
      {
        feature: "public_profile",
        steps: [
          () => this.fillInput("website_url", payload.website_url),
          () => this.fillTrue("is_teleconsultation", payload.is_teleconsultation),
          () => this.fillInput("featured_message", payload.featured_message),
          () => this.fillDescription(payload.description),
          () => this.fillMultiSelect("language_ids", payload.language_ids),
          () => this.fillMultiSelect("population_ids", payload.population_ids),
          () => this.fillMultiSelect("pathology_ids", payload.pathology_ids),
          () => this.fillRecords("diplomas", payload.diplomas),
          () => this.fillRecords("continuing_educations", payload.continuing_educations),
          () => this.fillRecords("academic_achievements", payload.academic_achievements)
        ]
      },
      {
        feature: null,
        steps: [
          () => this.fillRpps(payload.rpps_number),
          () => this.fillInput("professional_phone", payload.professional_phone)
        ]
      },
      {
        feature: "office",
        steps: [() => this.fillOffice(payload.office)]
      },
      {
        feature: "home_care",
        steps: [() => this.fillInput("home_care_honorary_attributes][home_care_price", payload.home_care_price)]
      },
      {
        feature: "public_profile",
        steps: [() => this.fillAvatar(payload.avatar_url)]
      }
    ]

    let filled = 0
    for (const group of groups) {
      let groupFilled = false
      for (const step of group.steps) {
        try {
          if (await step()) {
            filled += 1
            groupFilled = true
          }
        } catch (_error) {
          // This field failed; the next ones still run.
        }
      }
      if (groupFilled && group.feature) this.markFeatureFilled(group.feature)
    }

    if (this.homeCareFilled()) this.markFeatureFilled("home_care")
    this.enablePartnerIntegration()

    if (this.hasAppliedTarget) {
      this.appliedTarget.textContent = (this.messagesValue.applied || "").replace("%{count}", filled)
    }
    if (filled) scrollInContainer(this.scrollAnchor)
  }

  field (attribute) {
    return this.form.querySelector(`[name="${this.objectNameValue}[${attribute}]"]`)
  }

  // The office card replaces an earlier field: it sits further down the modal.
  noteScroll (element, options = {}) {
    if (!element) return
    if (!this.scrollAnchor || options.prefer) this.scrollAnchor = element
  }

  fillRpps (value) {
    const filled = this.fillInput("rpps_number", value)
    if (filled) this.markAutofilled(this.field("rpps_number")?.closest(".form-group"))
    return filled
  }

  fillInput (attribute, value) {
    const input = this.field(attribute)
    const filled = fillValue(input, value)
    if (filled) this.noteScroll(input)
    return filled
  }

  // The public profile defaults to Non. Doctolib only turns it on.
  fillTrue (attribute, value) {
    if (value !== true) return false

    const radio = this.form.querySelector(`[name="${this.objectNameValue}[${attribute}]"][value="true"]`)
    const filled = check(radio)
    if (filled) this.noteScroll(radio)
    return filled
  }

  fillDescription (html) {
    const hiddenField = this.field("description")
    if (!html || !hiddenField) return false

    const editor = hiddenField.closest("[data-controller~='wysiwyg']")
    const wysiwyg = editor && this.application.getControllerForElementAndIdentifier(editor, "wysiwyg")

    if (wysiwyg?.quill) {
      if (wysiwyg.quill.getText().trim()) return false

      wysiwyg.quill.clipboard.dangerouslyPasteHTML(html)
      wysiwyg.syncFormSubmission()
      this.noteScroll(editor)
      return true
    }

    if (hiddenField.value.trim()) return false

    hiddenField.value = html
    this.noteScroll(hiddenField)
    return true
  }

  fillMultiSelect (attribute, ids) {
    const select = this.form.querySelector(`select[name="${this.objectNameValue}[${attribute}][]"]`)
    if (!ids?.length || !select) return false

    const selected = Array.from(select.selectedOptions).map((option) => option.value)
    const missing = ids.map(String).filter((id) => !selected.includes(id))
    if (!missing.length) return false

    missing.forEach((id) => {
      if (select.tomselect) {
        select.tomselect.addItem(id)
      } else {
        const option = select.querySelector(`option[value="${id}"]`)
        if (option) option.selected = true
      }
    })
    this.noteScroll(select)
    return true
  }

  // Adds each record to the nested form tagged +data-doctolib-import+, reusing
  // untouched new rows and skipping records the form already lists.
  fillRecords (key, records) {
    const wrapper = this.form.querySelector(`[data-doctolib-import="${key}"]`)
    if (!records?.length || !wrapper) return false

    let filled = false
    records.forEach((record) => {
      const rows = visibleRows(wrapper)
      if (rows.some((row) => !isUntouchedRow(row) && sameRecord(row, record))) return

      const row = rows.find(isUntouchedRow) || this.addRow(wrapper)
      if (!row) return

      fillRow(row, record)
      filled = true
    })
    if (filled) this.noteScroll(wrapper)
    return filled
  }

  // Same insertion as rails-nested-form#add, with an index that stays unique
  // when several rows are added in the same millisecond.
  addRow (wrapper) {
    const template = wrapper.querySelector("[data-nested-form-target='template']")
    const target = wrapper.querySelector("[data-nested-form-target='target']")
    if (!template || !target) return null

    this.rowIndex = Math.max((this.rowIndex || 0) + 1, Date.now())
    target.insertAdjacentHTML("beforebegin", template.innerHTML.replace(/NEW_RECORD/g, this.rowIndex))
    return target.previousElementSibling
  }

  // Fills the unsaved office card, adding one when the practitioner has no
  // office yet; saved offices are left alone.
  async fillOffice (office) {
    if (!office) return false

    const card = await this.draftOfficeCard()
    if (!card) return false

    // Checked so the office is saved, and left folded: the header check shows it was filled.
    this.enableFeature("office")

    const name = fillValue(card.querySelector("input[name$='[office_attributes][name]']"), office.name)
    const address = await this.fillAddress(card, office)
    const extras = this.fillOfficeExtras(card, office)
    if (name || address || extras) this.noteScroll(this.featureSection("office") || card, { prefer: true })

    return name || address || extras
  }

  fillOfficeExtras (card, office) {
    const field = (key) => card.querySelector(`[data-office-field="${key}"]`)
    const honorary = office.honorary || {}
    const priceKeys = Object.keys(honorary).filter((key) => key !== "notes")
    const extras = [
      ...OFFICE_TEXT_FIELDS.map((key) => fillValue(field(key), office[key])),
      ...OFFICE_CHECKBOXES.map((key) => Boolean(office[key]) && check(field(key))),
      ...priceKeys.map((key) => fillValue(field(`honorary.${key}`), honorary[key])),
      fillPaymentMethods(card, office.payment_method_ids),
      fillOpeningDays(card, office.opening_days)
    ].some(Boolean)

    const more = card.querySelector("details")
    if (extras && more) more.open = true

    return extras
  }

  async draftOfficeCard () {
    // The nested-form template repeats this card inside a script: that copy is not a field.
    const draft = this.draftOfficeCards()[0]
    if (draft) return draft
    if (this.form.querySelector(".office-card:not(.office-card--draft)")) return null

    const wrapper = this.form.querySelector("[data-doctolib-import='offices']")
    const row = wrapper && this.addRow(wrapper)
    if (!row) return null

    // Lets Stimulus connect the controllers of the inserted card (address autocomplete).
    await new Promise((resolve) => setTimeout(resolve, 0))
    return row.querySelector(".office-card--draft") || row
  }

  featureSection (key) {
    return this.form.querySelector(`[data-feature-key="${key}"]`)
  }

  markFeatureFilled (key) {
    const section = this.featureSection(key)
    if (!section) return

    section.classList.add("is-filled")
    if (key === "public_profile" || key === "office") this.markAutofilled(section)
  }

  markAutofilled (element) {
    element?.classList.add("is-autofilled")
  }

  // Checks the feature without the change event: that event unfolds the panel.
  enableFeature (key) {
    const section = this.featureSection(key)
    if (!section) return

    const checkbox = section.querySelector("input[type='checkbox'][data-form--reveal-target~='input']")
    if (checkbox && !checkbox.checked) checkbox.checked = true

    const reveal = this.application.getControllerForElementAndIdentifier(section, "form--reveal")
    reveal?.syncManualFields()
  }

  // A published office or a filled home-care address is what partner integration requires.
  enablePartnerIntegration () {
    if (!this.publishedOfficeReady() && !this.homeCareFilled()) return

    this.enableFeature("partner_integration")
  }

  publishedOfficeReady () {
    const saved = Array.from(this.form.querySelectorAll(".office-card[data-office-published='true']"))
      .some((card) => !card.classList.contains("office-card--draft") && !card.closest("script, template"))
    if (saved) return true

    return this.draftOfficeCards().some((card) => {
      const published = card.querySelector("input[type='radio'][name$='[published]'][value='true']")
      if (published && !published.checked) return false

      const displayName = card.querySelector("[data-address-autocomplete-target='displayName']")
      return Boolean(displayName?.value.trim())
    })
  }

  homeCareFilled () {
    const displayName = this.featureSection("home_care")
      ?.querySelector("[data-address-autocomplete-target='displayName']")
    return Boolean(displayName?.value.trim())
  }

  draftOfficeCards () {
    return Array.from(this.form.querySelectorAll(".office-card--draft"))
      .filter((card) => !card.closest("script, template"))
  }

  // Writes the address immediately, then geocodes it for the coordinates the
  // office cannot be saved without. An address the admin already typed wins.
  async fillAddress (card, office) {
    const element = card.querySelector("[data-controller~='address-autocomplete']")
    const query = office?.display_name
    if (!element || !query) return false

    const input = element.querySelector("[data-address-autocomplete-target='input']")
    const city = element.querySelector("[data-address-autocomplete-target='city']")
    if (input?.value.trim() || city?.value.trim()) return false

    const target = (name) => element.querySelector(`[data-address-autocomplete-target='${name}']`)
    writeValue(input, query)
    writeValue(target("displayName"), query)
    writeValue(target("city"), office.city)
    writeValue(target("postalCode"), office.postal_code)
    writeValue(target("streetNumber"), office.street_number)
    writeValue(target("streetName"), office.street_name)
    writeValue(target("country"), "France")

    const autocomplete = this.application.getControllerForElementAndIdentifier(element, "address-autocomplete")
    if (autocomplete) await autocomplete.fillFromQuery(query)

    return true
  }

  async fillAvatar (url) {
    const input = this.form.querySelector(`input[type="file"][name^="${this.objectNameValue}[avatar"]`)
    if (!url || !input || input.files?.length) return false

    const preview = input.closest("[data-controller~='form--avatar-preview']")
      ?.querySelector("[data-form--avatar-preview-target='preview']")
    if (preview && !preview.hidden && preview.getAttribute("src")) return false

    try {
      const response = await fetch(url)
      const blob = response.ok ? await response.blob() : null
      if (!blob || !AVATAR_TYPES.includes(blob.type)) return false

      const transfer = new DataTransfer()
      transfer.items.add(new File([blob], `doctolib.${blob.type === "image/png" ? "png" : "jpg"}`, { type: blob.type }))
      input.files = transfer.files
      input.dispatchEvent(new Event("change", { bubbles: true }))
      this.noteScroll(preview || input)
      return true
    } catch (_error) {
      return false
    }
  }

  markStep (name, done) {
    this.stepTargets.find((step) => step.dataset.step === name)?.classList.toggle("is-done", done)
  }

  showStatus (message, colorClass, iconClass) {
    const status = document.createElement("p")
    status.className = `practitioner-form__doctolib-status ${colorClass}`
    const icon = document.createElement("i")
    icon.className = `fa ${iconClass}`
    icon.setAttribute("aria-hidden", "true")
    const text = document.createElement("span")
    text.textContent = message || ""
    status.append(icon, text)
    this.resultTarget.replaceChildren(status)
  }
}

// scrollIntoView moves the window, which a modal locks. The form scrolls inside .modal-body.
function scrollInContainer (element) {
  if (!element) return

  try {
    const container = element.closest(".modal-body")
    if (container && typeof container.scrollTo === "function") {
      const top = element.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 16
      container.scrollTo({ top: Math.max(top, 0), behavior: "smooth" })
      return
    }

    element.scrollIntoView({ block: "start", behavior: "smooth" })
  } catch (_error) {
    // The fields are filled even when the page cannot scroll.
  }
}

function csrfToken () {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

function blank (value) {
  return value === undefined || value === null || String(value).trim() === ""
}

function setValue (input, value) {
  if (!input || blank(value)) return false

  input.value = value
  input.dispatchEvent(new Event("input", { bubbles: true }))
  input.dispatchEvent(new Event("change", { bubbles: true }))
  return true
}

// Only fills empty inputs: the admin's own entries win over Doctolib.
function fillValue (input, value) {
  if (!input || !blank(input.value)) return false

  return setValue(input, value)
}

// Same as fillValue, without events: address fields must not start a search
// before the geocoder runs.
function writeValue (input, value) {
  if (!input || blank(value) || !blank(input.value)) return false

  input.value = value
  return true
}

function check (input) {
  if (!input || input.checked) return false

  input.checked = true
  input.dispatchEvent(new Event("change", { bubbles: true }))
  return true
}

function fillPaymentMethods (card, ids) {
  const boxes = Array.from(card.querySelectorAll("[data-office-payment-method]"))
  if (!ids?.length || boxes.some((box) => box.checked)) return false

  return ids.map((id) => check(boxes.find((box) => box.value === String(id)))).some(Boolean)
}

// The office card has a fixed number of slots per weekday; extra slots are dropped.
function fillOpeningDays (card, days) {
  const inputs = Array.from(card.querySelectorAll("[data-opening-slot]"))
  if (!days?.length || inputs.some((input) => input.value)) return false

  const slotsPerDay = {}
  return days.map(({ wday, start_at: startAt, end_at: endAt }) => {
    const slot = slotsPerDay[wday] ?? 0
    slotsPerDay[wday] = slot + 1
    const input = (bound) => inputs.find((element) => element.dataset.openingSlot === [wday, slot, bound].join("-"))

    return [setValue(input("start_at"), startAt), setValue(input("end_at"), endAt)].some(Boolean)
  }).some(Boolean)
}

function rowInput (row, attribute) {
  return row.querySelector(`[name$="[${attribute}]"]`)
}

function visibleRows (wrapper) {
  return Array.from(wrapper.querySelectorAll(".nested-form__item"))
    .filter((row) => rowInput(row, "_destroy")?.value !== "1")
}

function rowYear (row) {
  return (rowInput(row, "obtention_year") || rowInput(row, "year"))?.value || ""
}

function sameRecord (row, record) {
  const title = rowInput(row, "title")?.value.trim().toLowerCase()
  const year = record.obtention_year ?? record.year

  return title === record.title.trim().toLowerCase() && (blank(year) || rowYear(row) === String(year))
}

// A row added by the admin but left as rendered (a diploma row comes with a default title).
function isUntouchedRow (row) {
  const title = rowInput(row, "title")
  return row.dataset.newRecord === "true" && !rowYear(row) && (!title || title.value === title.defaultValue)
}

function fillRow (row, record) {
  Object.entries(record).forEach(([attribute, value]) => {
    if (RECORD_PROVIDER_FIELDS.includes(attribute)) return

    setValue(rowInput(row, attribute), value)
  })
  fillProvider(row, record)
}

// Picks the provider when the select lists it, otherwise "other" with the name typed in.
function fillProvider (row, { education_provider_id: id, custom_education_provider: custom }) {
  const select = row.querySelector("select[name$='[education_provider_id]']")
  if (!select || (blank(id) && blank(custom))) return

  const listed = !blank(id) && select.querySelector(`option[value="${id}"]`)
  const value = listed ? String(id) : OTHER_EDUCATION_PROVIDER
  if (select.tomselect) {
    select.tomselect.setValue(value)
  } else {
    select.value = value
  }
  select.dispatchEvent(new Event("change", { bubbles: true }))
  if (listed) return

  setValue(rowInput(row, "custom_education_provider"), custom)
  const reveal = row.querySelector("[data-form--reveal-target='reveal']")
  if (reveal) reveal.hidden = false
}
